import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { QrCode, RefreshCw, Search, Check, Wifi, WifiOff, Loader2 } from "lucide-react";
import { errMsg } from "@/lib/api";

const btn = "px-4 h-10 rounded-lg text-sm font-semibold inline-flex items-center gap-2 active:scale-95 transition-transform disabled:opacity-60";

function DeviceStatus({ req, onConnected }) {
  const [dev, setDev] = useState(null);
  const [qr, setQr] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => req("get", "/staff/fonnte/device").then((d) => {
    setDev(d);
    if (d.device_status === "connect") { setQr(""); onConnected?.(); }
  }).catch((e) => setDev({ error: errMsg(e) })), [req, onConnected]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!qr) return undefined;
    const t = setInterval(load, 5000);
    const r = setInterval(() => req("post", "/staff/fonnte/qr").then((d) => d.qr && setQr(d.qr)).catch(() => {}), 30000);
    return () => { clearInterval(t); clearInterval(r); };
  }, [qr, load, req]);

  const scan = async () => {
    setBusy(true);
    try {
      const d = await req("post", "/staff/fonnte/qr");
      if (d.connected) { toast.success("WhatsApp sudah bersambung"); load(); } else setQr(d.qr);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const logout = async () => {
    if (!window.confirm("Putuskan sambungan WhatsApp dari Fonnte?")) return;
    try { await req("post", "/staff/fonnte/disconnect"); toast.success("Sambungan diputuskan"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const on = dev?.device_status === "connect";

  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="fonnte-device-box">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm font-medium flex items-center gap-2">
          {on ? <Wifi className="w-4 h-4 text-green-600" /> : <WifiOff className="w-4 h-4 text-red-500" />}
          <span data-testid="fonnte-device-status">{!dev ? "Menyemak..." : dev.error ? dev.error : on ? `Bersambung${dev.device ? ` · ${dev.device}` : ""}` : "Belum bersambung"}</span>
        </p>
        <div className="flex gap-2">
          {!on && <button onClick={scan} disabled={busy} data-testid="fonnte-scan-qr" className={`${btn} bg-[#B91C1C] text-white`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <QrCode className="w-4 h-4" />}Imbas QR</button>}
          {on && <button onClick={logout} data-testid="fonnte-disconnect" className={`${btn} border text-red-600`}>Putuskan</button>}
          <button onClick={load} data-testid="fonnte-refresh-status" className={`${btn} border`}><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      {dev && !dev.error && (dev.quota || dev.expired) && <p className="text-xs text-slate-500">Kuota: {dev.quota ?? "-"} · Tamat: {dev.expired ?? "-"} · Pakej: {dev.package ?? "-"}</p>}
      {qr && (
        <div className="flex flex-col items-center gap-2 py-2" data-testid="fonnte-qr-box">
          <img src={`data:image/png;base64,${qr}`} alt="QR WhatsApp" className="w-60 h-60 border rounded-lg bg-white" data-testid="fonnte-qr-image" />
          <p className="text-xs text-slate-500 text-center">Buka WhatsApp di telefon → Peranti Dipautkan → Pautkan Peranti, kemudian imbas QR ini.</p>
        </div>
      )}
    </div>
  );
}

function GroupPicker({ req, value, onPick }) {
  const [groups, setGroups] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = async (refresh) => {
    setBusy(true);
    try {
      const d = await req("post", `/staff/fonnte/groups${refresh ? "?refresh=true" : ""}`);
      setGroups(d.groups);
      if (d.note) toast.message(`Fonnte: ${d.note}`);
    } catch (e) { setGroups([]); toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const shown = useMemo(() => (groups || []).filter((g) => g.name.toLowerCase().includes(q.toLowerCase()) || g.id.includes(q)), [groups, q]);

  return (
    <div className="rounded-xl border p-4 space-y-3" data-testid="fonnte-group-picker">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-medium">Group untuk notifikasi pesanan</p>
        <div className="flex gap-2">
          <button onClick={() => load(false)} disabled={busy} data-testid="fonnte-load-groups" className={`${btn} border`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Senarai Group</button>
          <button onClick={() => load(true)} disabled={busy} data-testid="fonnte-refresh-groups" className={`${btn} border`}><RefreshCw className="w-4 h-4" />Muat Semula dari WhatsApp</button>
        </div>
      </div>
      {value && <p className="text-xs text-slate-600" data-testid="fonnte-selected-group">Dipilih: <b>{groups?.find((g) => g.id === value)?.name || value}</b></p>}
      {groups && (
        <>
          <label className="flex items-center gap-2 h-10 px-3 rounded-lg border focus-within:ring-2 focus-within:ring-[#B91C1C]">
            <Search className="w-4 h-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama group" data-testid="fonnte-group-search" className="flex-1 outline-none text-sm bg-transparent" />
          </label>
          <div className="max-h-64 overflow-y-auto divide-y border rounded-lg" data-testid="fonnte-group-list">
            {shown.length === 0 && <p className="p-3 text-sm text-slate-500">Tiada group dijumpai. Tekan "Muat Semula dari WhatsApp".</p>}
            {shown.map((g) => (
              <button key={g.id} type="button" onClick={() => onPick(g.id)} data-testid={`fonnte-group-${g.id}`}
                className={`w-full text-left px-3 py-2.5 text-sm flex items-center justify-between gap-2 hover:bg-red-50 ${value === g.id ? "bg-red-50" : ""}`}>
                <span><span className="font-medium">{g.name}</span><span className="block text-[11px] text-slate-400">{g.id}</span></span>
                {value === g.id && <Check className="w-4 h-4 text-red-600 shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
      {value && <button type="button" onClick={() => onPick("")} data-testid="fonnte-clear-group" className="text-xs text-red-600">Buang pilihan group</button>}
    </div>
  );
}

export function FonntePanel({ req, s, set }) {
  const pick = async (id) => {
    try {
      await req("put", "/staff/wa-group", { group_id: id });
      set("group_id", id);
      toast.success(id ? "Group disimpan untuk notifikasi" : "Pilihan group dibuang");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!s.fonnte_configured) return <p className="text-sm text-amber-700" data-testid="fonnte-not-configured">Token Fonnte belum ditetapkan. Minta superadmin masukkan token peranti Fonnte.</p>;
  return (
    <div className="space-y-3">
      <DeviceStatus req={req} />
      {!s.group_id && !s.admin_number && <p className="text-sm text-red-600 font-medium" data-testid="fonnte-no-target">Belum ada group atau nombor admin dipilih — notifikasi pesanan tidak akan dihantar.</p>}
      <GroupPicker req={req} value={s.group_id} onPick={pick} />
    </div>
  );
}
